import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, ListItem, Chip, Paper, IconButton, Typography, Box, Collapse } from '@material-ui/core';
import { Visibility, VisibilityOff, ExpandMore, ExpandLess } from '@material-ui/icons';
import TransectShapeList from './TransectShapeList';
import { overrideShapeStyle } from '../../../../common/map';
import { BRBG_11 } from '../../../../common/utils';

const applyFeatureHighlight = (features = [], highlighted) => {
  features.forEach((feature) => {
    try {
      feature.setProperty('highlighted', highlighted);
    } catch (e) {
      console.error('applyFeatureHighlight error', e);
    }
  });
};

const applyBaselineHighlight = (baselineOverlay, highlighted) => {
  if (!baselineOverlay) return;
  try {
    baselineOverlay.setOptions({ strokeWeight: highlighted ? 4 : 2 });
  } catch (e) {
    console.error('applyBaselineHighlight error', e);
  }
};

const applyROIHighlight = (roiPolygon, highlighted) => {
  if (!roiPolygon) return;
  try {
    roiPolygon.setOptions({
      strokeWeight: highlighted ? 3 : 1.5,
      fillOpacity: highlighted ? 0.35 : 0.2,
    });
  } catch (e) {
    console.error('applyROIHighlight error', e);
  }
};

// Stable reference for the `customLayers` default — an inline `= []` default
// is a brand-new array on every render, which breaks the `useEffect`
const EMPTY_CUSTOM_LAYERS = [];

/**
 * @function ShapeList
 * @description Component for managing the visibility of shapes on the map.
 * @param {Object} props.shapesGroup - Array of shape groups containing overlays and properties.
 * @param {Object} props.baseLine - Baseline object containing overlay and properties.
 * @param {Object} props.roiCoords - Coordinates of the region of interest polygon.
 * @param {Object} props.drawingManager - Reference to the Google Maps Drawing Manager instance.
 * @param {Array} props.customLayers - Array of custom layers with visibility controls.
 * @param {Function} props.onCustomLayerToggle - Callback when custom layer visibility changes.
 * *Called by:
 * @see ProcessingPage
 * @returns {JSX.Element} Rendered ShapeList component.
 *
 */
const ShapeList = ({
  shapesGroup,
  baseLine,
  roiCoords,
  drawingManager,
  customLayers = EMPTY_CUSTOM_LAYERS,
  onCustomLayerToggle,
  colorbarData,
  transectLegendVariant,
  embedded = false,
}) => {
  const classes = useStyles();
  const [t] = useTranslation();

  const [showShapes, setShowShapes] = useState(true);
  const [hiddenGroups, setHiddenGroups] = useState([]);
  const [roiVisible, setRoiVisible] = useState(true);
  const [baselineVisible, setBaselineVisible] = useState(true);
  const [customLayersState, setCustomLayersState] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    if (!customLayers || customLayers.length == 0) return;
    setCustomLayersState(customLayers);
    setExpandedGroups((prev) => {
      const auto = {};
      customLayers?.forEach((l) => {
        if (l.type === 'group' && !(l.id in prev)) auto[l.id] = true;
      });
      return Object.keys(auto).length ? { ...prev, ...auto } : prev;
    });
  }, [customLayers]);

  /**
   * @function updateMapDrawings
   * @description Updates the visibility and styles of map drawings based on the provided parameters.
   * @param {Object} params - Parameters for updating map drawings.
   * @param {Array} params.shapesGroup - Array of shape groups to update.
   * @param {boolean} params.showShapes - Flag indicating whether to show or hide the shapes.
   * @param {Object} params.baseLine - Baseline object containing overlay and properties.
   * @param {Object} params.roiCoords - Coordinates of the region of interest polygon.
   * @param {Object} params.drawingManager - Reference to the Google Maps Drawing Manager instance.
   * @returns {void}
   */
  const updateMapDrawings = ({
    shapesGroup: groups,
    showShapes: visible,
    drawingManager: dm,
    changeROIandBaseline = true,
  }) => {
    if (!groups || groups.length === 0) return;

    const applyStyle = dm?.current?.map
      ? (feature, style) => dm.current.map.data.overrideStyle(feature, style)
      : overrideShapeStyle;
    try {
      groups.forEach((group) => {
        group.overlays.forEach((feature) => {
          const oldStyle = feature.getProperty('customStyle') || {};
          const newStyle = {
            ...oldStyle,
            fillOpacity: visible ? 1 : 0,
            strokeOpacity: visible ? 1 : 0,
          };
          applyStyle(feature, { ...newStyle, visible: true });
        });
      });
      if (!changeROIandBaseline) return;
      baseLine?.overlay?.setOptions({ strokeOpacity: visible ? 1 : 0 });
      roiCoords?.current?.setOptions({
        strokeOpacity: visible ? 0.6 : 0,
        fillOpacity: visible ? 0.2 : 0,
      });
    } catch (e) {
      console.error('updateMapDrawings error', e);
    }
  };

  /**
   * @function toggleGroupVisibility
   * @description Toggles the visibility of a specific shape group on the map.
   * @param {Object} item - Shape group item to toggle.
   * @param {number} index - Index of the shape group in the shapesGroup array.
   * @returns {void}
   */

  const toggleGroupVisibility = (item, index) => {
    const isHidden = hiddenGroups.includes(index);
    const newVisibility = isHidden;
    updateMapDrawings({
      shapesGroup: [item],
      showShapes: newVisibility,
      drawingManager,
      changeROIandBaseline: false,
    });
    setHiddenGroups((prev) => (newVisibility ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  /**
   * @function toggleBaselineVisibility
   * @description Toggles the visibility of the baseline on the map.
   * @returns {void}
   */
  const toggleBaselineVisibility = () => {
    const newVisibility = !baselineVisible;
    setBaselineVisible(newVisibility);
    baseLine?.overlay?.setOptions({ strokeOpacity: newVisibility ? 1 : 0 });
  };

  /**
   * @function toggleRoiVisibility
   * @description Toggles the visibility of the region of interest (ROI) on the map.
   * @returns {void}
   */
  const toggleRoiVisibility = () => {
    const newVisibility = !roiVisible;
    setRoiVisible(newVisibility);
    roiCoords?.current?.setOptions({
      strokeOpacity: newVisibility ? 0.6 : 0,
      fillOpacity: newVisibility ? 0.2 : 0,
    });
  };

  /**
   * @function toggleAllVisibility
   * @description Toggles the visibility of all shapes, baseline, and ROI on the map.
   * @see updateMapDrawings
   * @returns {void}
   */
  const toggleAllVisibility = () => {
    const newVisibility = !showShapes;
    setShowShapes(newVisibility);
    setRoiVisible(newVisibility);
    setBaselineVisible(newVisibility);
    if (!shapesGroup) return;
    shapesGroup.forEach((group) => {
      updateMapDrawings({ shapesGroup: [group], showShapes: newVisibility, drawingManager });
    });
    setHiddenGroups(newVisibility ? [] : shapesGroup.map((_, i) => i));
  };

  /**
   * @function toggleCustomLayerVisibility
   * @description Toggles the visibility of a custom layer
   * @param {Object} layer - The custom layer object to toggle.
   * @returns {void}
   */
  const toggleCustomLayerVisibility = (layer) => {
    if (!layer) return;
    const newVisibility = !layer.visible;

    // Toggle polyline(s) visibility on the map if present
    layer.polyline?.setVisible(newVisibility);
    layer.polylines?.forEach((p) => p.setVisible(newVisibility));

    setCustomLayersState((prev) =>
      prev.map((group) => {
        if (group.id === layer.id) {
          return { ...group, visible: newVisibility };
        }
        if (group.type === 'group' && group.children) {
          return {
            ...group,
            children: group.children.map((child) =>
              child.id === layer.id ? { ...child, visible: newVisibility } : child,
            ),
          };
        }
        return group;
      }),
    );
    if (onCustomLayerToggle) onCustomLayerToggle(layer.id, newVisibility);
  };

  const toggleCustomLayerGroup = (group) => {
    const someVisible = group.children?.some((c) => c.visible);
    const newVis = !someVisible;
    group.children?.forEach((child) => {
      child.polylines?.forEach((p) => p.setVisible(newVis));
      if (onCustomLayerToggle) onCustomLayerToggle(child.id, newVis);
    });
    setCustomLayersState((prev) =>
      prev.map((g) =>
        g.id === group.id ? { ...g, children: g.children?.map((c) => ({ ...c, visible: newVis })) } : g,
      ),
    );
  };

  return (
    <Paper
      className={classes.wrapper}
      elevation={embedded ? 0 : 1}
      style={{ padding: 10, ...(embedded ? { backgroundColor: 'transparent', backgroundImage: 'none' } : {}) }}>
      {shapesGroup?.length > 0 && (
        <Grid id="all_shapes_visibility" container direction="row" alignItems="center" spacing={1}>
          <Grid item>
            <Typography variant="subtitle2">{t('forms.map.showAll')}</Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={toggleAllVisibility} size="small">
              {showShapes ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
          </Grid>
        </Grid>
      )}
      <Grid
        id="roi_visibility"
        container
        direction="row"
        alignItems="center"
        spacing={1}
        onMouseOver={() => applyROIHighlight(roiCoords?.current, true)}
        onMouseOut={() => applyROIHighlight(roiCoords?.current, false)}>
        <Grid item>
          <Typography variant="body2">{t('forms.map.roi')}</Typography>
        </Grid>
        <Grid item>
          <IconButton onClick={toggleRoiVisibility} size="small">
            {roiVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
          </IconButton>
        </Grid>
      </Grid>
      {baseLine !== null && shapesGroup?.length > 0 && (
        <Grid
          id="baseline_visibility"
          container
          direction="row"
          alignItems="center"
          spacing={1}
          onMouseOver={() => applyBaselineHighlight(baseLine?.overlay, true)}
          onMouseOut={() => applyBaselineHighlight(baseLine?.overlay, false)}>
          <Grid item>
            <Typography variant="body2">{t('forms.map.baseline')}</Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={toggleBaselineVisibility} size="small">
              {baselineVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
          </Grid>
        </Grid>
      )}
      {customLayersState?.map((layer) => {
        if (layer.type === 'group') {
          return (
            <div
              key={layer.id}
              style={{ cursor: 'default' }}
              onMouseEnter={() => {
                layer.children?.forEach((child) =>
                  child.polylines?.forEach((p) =>
                    p.setOptions({
                      icons: [
                        { icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 4 }, offset: '0', repeat: '10px' },
                      ],
                    }),
                  ),
                );
              }}
              onMouseLeave={() => {
                layer.children?.forEach((child) =>
                  child.polylines?.forEach((p) =>
                    p.setOptions({
                      icons: [
                        { icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 2 }, offset: '0', repeat: '10px' },
                      ],
                    }),
                  ),
                );
              }}>
              <Grid
                container
                direction="row"
                alignItems="center"
                spacing={1}
                style={{ marginTop: 8, cursor: 'pointer' }}
                onClick={() => setExpandedGroups((prev) => ({ ...prev, [layer.id]: !prev[layer.id] }))}>
                <Grid item xs>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {layer.name}
                  </Typography>
                  {layer.subtitle && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {layer.subtitle}
                    </Typography>
                  )}
                </Grid>
                {layer.singleGroupToggle && (
                  <Grid item>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCustomLayerGroup(layer);
                      }}
                      size="small">
                      {layer.children?.some((c) => c.visible) ? (
                        <Visibility fontSize="small" />
                      ) : (
                        <VisibilityOff fontSize="small" />
                      )}
                    </IconButton>
                  </Grid>
                )}
                <Grid item>
                  <IconButton size="small">
                    {expandedGroups[layer.id] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                </Grid>
              </Grid>
              <Collapse in={!!expandedGroups[layer.id]}>
                {layer.children?.map((child) => (
                  <ListItem key={child.id} disableGutters style={{ paddingLeft: 16, paddingTop: 4, paddingBottom: 4 }}>
                    <Grid container direction="row" alignItems="center">
                      {child.color && (
                        <Box style={{ width: 20, height: 3, backgroundColor: child.color, marginRight: 8 }} />
                      )}
                      <Grid item xs>
                        <Typography variant="body2" style={{ fontSize: '0.85rem' }}>
                          {child.name}
                        </Typography>
                      </Grid>
                      {!layer.singleGroupToggle && (
                        <Grid item>
                          <IconButton onClick={() => toggleCustomLayerVisibility(child)} size="small">
                            {child.visible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  </ListItem>
                ))}
              </Collapse>
            </div>
          );
        }
        return (
          <Grid key={layer.id} container direction="row" alignItems="center" spacing={1} style={{ marginTop: 8 }}>
            {layer.icon && <Grid item>{layer.icon}</Grid>}
            <Grid item xs>
              <Typography variant="body2">
                {layer.name}
                {layer.metadata?.threshold !== undefined && (
                  <span style={{ fontWeight: 'bold', marginLeft: 4 }}>
                    - {t('forms.intertidal.threshold')}: {layer.metadata.threshold.toFixed(4)}
                  </span>
                )}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={() => toggleCustomLayerVisibility(layer)} size="small">
                {layer.visible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
              </IconButton>
            </Grid>
          </Grid>
        );
      })}
      {shapesGroup?.map((item, originalIndex) => {
        const isHidden = hiddenGroups.includes(originalIndex);
        return (
          <ListItem
            key={originalIndex}
            disableGutters
            onMouseOver={(e) => {
              e.stopPropagation();
              applyFeatureHighlight(item.overlays, true);
            }}
            onMouseOut={(e) => {
              e.stopPropagation();
              applyFeatureHighlight(item.overlays, false);
            }}>
            <Grid container direction="column" className={classes.item}>
              <Grid
                container
                direction="row"
                alignItems="center"
                className={classes.item}
              >
                {item.overlays?.[0]?.overlay?.cassie_color && (
                  <Grid item>
                    <Box
                      style={{
                        width: 20,
                        height: 3,
                        backgroundColor: item.overlays[0].overlay.cassie_color,
                        marginRight: 8,
                      }}
                    />
                  </Grid>
                )}
                <Grid item>
                  <Typography variant="body2">{t(item.name)}</Typography>
                </Grid>
                <Grid item>
                  <Chip
                    className={classes.chip}
                    size="small"
                    label={
                      item.overlays.length === 1
                        ? `1 ${t('forms.map.item.s')}`
                        : `${item.overlays.length} ${t('forms.map.item.p')}`
                    }
                  />
                </Grid>
                <Grid item>
                  <IconButton onClick={() => toggleGroupVisibility(item, originalIndex)} size="small">
                    {isHidden ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </Grid>
              </Grid>
              {item.name === "forms.map.transects.title" && (
                <TransectShapeList variant={transectLegendVariant} />
              )}
              {item.name === "forms.map.shorelines" && colorbarData && (
                <Box style={{ marginTop: 4, marginBottom: 2 }}>
                  <Box
                    style={{
                      height: 10,
                      borderRadius: 4,
                      background: `linear-gradient(to right, ${BRBG_11.join(', ')})`,
                    }}
                  />
                  <Grid container justifyContent="space-between" style={{ marginTop: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      {colorbarData.start}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {colorbarData.end}
                    </Typography>
                  </Grid>
                </Box>
              )}
            </Grid>
          </ListItem>
        );
      })}
    </Paper>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles.
 * Defines CSS-in-JS styles for processing page components.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginBottom: 0,
    marginLeft: 12,
  },
  item: {
    margin: 0,
    padding: 0,
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  roiControl: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

export default ShapeList;
